import { ulid } from '@/api/ulid';

/**
 * Start-run drawer MODEL (pure): the local validators over the ratified POST /v1/runs body — the
 * subject entity ULID shape, the input JSON object rule, and the caller-chosen run id. Kept apart
 * from the component so the rules are testable without React.
 */

/** Canonical 26-char Crockford ULID, first char 0–7 (the run index binds `entityId` varchar(26)). */
export const ENTITY_ULID = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

/** Validate the optional input JSON: empty is fine; otherwise a JSON OBJECT. */
export function parseRunInput(text: string): { ok: true; value?: Record<string, unknown> } | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true };
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'Input must be a JSON object (e.g. {"channel": "branch"}).' };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (e) {
    return { ok: false, error: `Input is not valid JSON: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/** Validate the optional entity id: empty is fine; otherwise a canonical ULID. */
export function entityIdProblem(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (!ENTITY_ULID.test(v)) {
    return 'Entity id must be a 26-character ULID (Crockford base32, first character 0–7).';
  }
  return null;
}

/** A fresh caller-chosen run id: the run's identity and the contract's natural idempotency key. */
export function freshRunId(): string {
  return `run-${ulid()}`;
}
